import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { api, type ResumenResenas } from '@/lib/api';
import { fmt } from '@/lib/analyticsDates';
import { useIsDarkMode } from '@/hooks/useIsDarkMode';
import type { AnalyticsResult, CompareResult, FacturacionSerenoResult, HistoricoReservasResult } from '@/lib/types';
import { classNames } from '@/lib/utils';

/**
 * El negocio del mes en curso, del día 1 a hoy, frente al mismo tramo del año
 * anterior. Con los filtros por defecto de Finance (pisos activos, sin
 * Alicante, reparto por noches) para que las cifras sean las que se ven al
 * abrir esa pantalla.
 *
 * Se pide aparte y después del primer pintado: son las consultas más pesadas
 * del Dashboard, y lo pendiente no tiene por qué esperarlas. Cada pieza con su
 * respaldo: si falla una, las demás se pintan igual.
 */

const FILTROS = { activeOnly: true, alicante: 'sin' as const, prorate: true };

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** «Direct», «Website» y «Tu Sereno» son un solo canal (regla de Matías, igual que Analytics › Reservas). */
const CANALES_PROPIOS = /^\s*(direct|website|tu\s*sereno)\s*$/i;

const COLOR_CANAL: Record<string, string> = {
  Airbnb: '#2A78D6',
  Booking: '#EB6834',
  'Tu Sereno': '#1BAF7A',
  Vrbo: '#EDA100',
};

/** `84.320 €`: sin decimales y con punto de miles también con cuatro cifras. */
export function euros(n: number): string {
  const r = Math.round(n);
  return `${r < 0 ? '−' : ''}${String(Math.abs(r)).replace(/\B(?=(\d{3})+(?!\d))/g, '.')} €`;
}

function decimal(n: number, decimales = 2): string {
  return n.toLocaleString('es-ES', { minimumFractionDigits: decimales, maximumFractionDigits: decimales });
}

function nombreCanal(canal: string): string {
  if (CANALES_PROPIOS.test(canal)) return 'Tu Sereno';
  if (/airbnb/i.test(canal)) return 'Airbnb';
  if (/booking/i.test(canal)) return 'Booking';
  if (/vrbo|homeaway/i.test(canal)) return 'Vrbo';
  return canal || 'Otros';
}

interface Negocio {
  compare: CompareResult | null;
  facturacion: FacturacionSerenoResult | null;
  historico: HistoricoReservasResult | null;
  resenas: ResumenResenas | null;
  anio: AnalyticsResult | null;
}

function fechas(hoy = new Date()) {
  const y = hoy.getFullYear();
  const m = hoy.getMonth();
  const d = hoy.getDate();
  // El mismo tramo hace un año; un 29 de febrero se queda en el 28
  const ultimoDiaAnterior = new Date(y - 1, m + 1, 0).getDate();
  return {
    hoy: fmt(hoy),
    inicioMes: fmt(new Date(y, m, 1)),
    finMes: fmt(new Date(y, m + 1, 0)),
    inicioAnterior: fmt(new Date(y - 1, m, 1)),
    hoyAnterior: fmt(new Date(y - 1, m, Math.min(d, ultimoDiaAnterior))),
    inicio12: fmt(new Date(y, m - 11, 1)),
    dia: d,
    mes: m,
    anio: y,
  };
}

async function cargarNegocio(): Promise<Negocio> {
  const f = fechas();
  const [compare, facturacion, historico, resenas, anio] = await Promise.allSettled([
    api.compareAnalytics({
      fromA: f.inicioMes, toA: f.hoy, labelA: 'Este mes',
      fromB: f.inicioAnterior, toB: f.hoyAnterior, labelB: 'Hace un año',
      ...FILTROS,
    }),
    // Hasta fin de mes: lo que sale después de hoy y la gestión del mes, que se
    // factura el último día, vienen como «previsto»
    api.getFacturacionSereno({ from: f.inicioMes, to: f.finMes, activeOnly: FILTROS.activeOnly, alicante: FILTROS.alicante }),
    // Solo para la cartera (entradas después de hoy): no hace falta recorrer el pasado
    api.getHistoricoReservas({ from: f.hoy, activeOnly: FILTROS.activeOnly, alicante: FILTROS.alicante }),
    api.getResumenResenas({ rango: '12m' }),
    api.getAnalytics({ from: f.inicio12, to: f.hoy, ...FILTROS }),
  ]);
  const valor = <T,>(r: PromiseSettledResult<T>): T | null => (r.status === 'fulfilled' ? r.value : null);
  return {
    compare: valor(compare),
    facturacion: valor(facturacion),
    historico: valor(historico),
    resenas: valor(resenas),
    anio: valor(anio),
  };
}

/** Flecha y cifra: el sentido no va solo en el color. */
function Variacion({ valor, unidad = '%', grande = false }: { valor: number | null | undefined; unidad?: string; grande?: boolean }) {
  if (valor == null || !Number.isFinite(valor)) return null;
  const r = Math.round(Math.abs(valor));
  const sube = valor > 0 && r > 0;
  const baja = valor < 0 && r > 0;
  const texto = unidad === 'puntos' ? `${r} ${r === 1 ? 'punto' : 'puntos'}` : `${r} %`;
  return (
    <span
      className={classNames(
        'inline-flex items-center gap-0.5 font-semibold',
        grande ? 'text-sm rounded-full px-2 py-0.5' : 'text-xs',
        sube ? 'text-green-700' : baja ? 'text-red-700' : 'text-sereno-dark/60',
        grande && (sube ? 'bg-green-50' : baja ? 'bg-red-50' : 'bg-gray-100'),
      )}
    >
      {sube && <ArrowUp size={grande ? 13 : 12} strokeWidth={2.5} aria-hidden="true" />}
      {baja && <ArrowDown size={grande ? 13 : 12} strokeWidth={2.5} aria-hidden="true" />}
      <span className="sr-only">{sube ? 'Sube' : baja ? 'Baja' : 'Igual'}:</span>
      {sube || baja ? texto : 'igual'}
      <span className="sr-only"> frente a hace un año</span>
    </span>
  );
}

function NoDisponible({ que }: { que: string }) {
  return <p className="text-xs text-sereno-dark/50">{que}: no disponible ahora.</p>;
}

function Barra({ className }: { className?: string }) {
  return <span className={classNames('block rounded bg-gray-100 animate-pulse', className)} />;
}

function Tesela({ titulo, valor, pie }: { titulo: string; valor: string; pie?: React.ReactNode }) {
  return (
    <div className="border border-gray-100 rounded-lg px-3.5 py-3 min-w-0">
      <p className="text-xs text-sereno-dark/60">{titulo}</p>
      <p className="mt-0.5 text-xl font-bold tabular-nums">{valor}</p>
      {pie && <div className="mt-0.5">{pie}</div>}
    </div>
  );
}

export default function BloqueNegocio({ vuelta, className }: { vuelta: number; className?: string }) {
  const [negocio, setNegocio] = useState<Negocio | null>(null);
  const oscuro = useIsDarkMode();
  const f = fechas();

  useEffect(() => {
    let vigente = true;
    // Después del primer pintado: primero se ve lo pendiente
    const t = window.setTimeout(() => {
      cargarNegocio().then((n) => { if (vigente) setNegocio(n); });
    }, 0);
    return () => { vigente = false; window.clearTimeout(t); };
  }, [vuelta]);

  const cur = negocio?.compare?.current;
  const prev = negocio?.compare?.previous;
  const deltas = negocio?.compare?.deltas;
  const revpar = (p?: { adr: number; ocupacion: number }) => (p ? (p.adr * p.ocupacion) / 100 : 0);
  const pctRevpar = cur && prev && revpar(prev) > 0 ? ((revpar(cur) - revpar(prev)) / revpar(prev)) * 100 : null;

  // Los últimos doce meses, también los que no tuvieron nada
  const porMes = new Map((negocio?.anio?.byMonth || []).map((m) => [m.month, m.facturacion]));
  const meses = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(f.anio, f.mes - 11 + i, 1);
    const clave = fmt(d).slice(0, 7);
    return { clave, mes: d.getMonth(), anio: d.getFullYear(), valor: porMes.get(clave) || 0, actual: i === 11 };
  });
  const maximo = Math.max(1, ...meses.map((m) => m.valor));
  const mesMaximo = meses.reduce((a, b) => (b.valor > a.valor ? b : a), meses[0]);
  const k = (n: number) => `${Math.round(n / 1000)}k`;
  const nombreLargo = (m: { mes: number; anio: number; actual: boolean }) =>
    m.actual ? `${MESES[m.mes]}, hasta el ${f.dia}` : `${MESES[m.mes]}${m.anio !== f.anio ? ` de ${m.anio}` : ''}`;
  const colorBarra = (actual: boolean) => (actual ? (oscuro ? '#D1E0F0' : '#0B1D2E') : (oscuro ? '#4A7BB0' : '#6B8FBF'));

  // Canales del mes, con Direct/Website/Tu Sereno juntos
  const canales = (() => {
    const suma = new Map<string, number>();
    for (const c of cur?.byChannel || []) suma.set(nombreCanal(c.channel), (suma.get(nombreCanal(c.channel)) || 0) + c.facturacion);
    const total = [...suma.values()].reduce((a, b) => a + b, 0);
    if (total <= 0) return [];
    const filas = [...suma.entries()].map(([nombre, v]) => ({ nombre, pct: (v / total) * 100 })).sort((a, b) => b.pct - a.pct);
    // Los cuatro primeros con nombre; el resto, «Otros»
    const cabeza = filas.slice(0, 4);
    const resto = filas.slice(4).reduce((s, x) => s + x.pct, 0);
    return [...cabeza, ...(resto > 0.5 ? [{ nombre: 'Otros', pct: resto }] : [])]
      .map((c) => ({ ...c, color: COLOR_CANAL[c.nombre] || (oscuro ? '#6B7280' : '#9AA3AD'), redondo: Math.round(c.pct) }));
  })();

  const cartera = negocio?.historico?.cartera;
  const resenas = negocio?.resenas?.global;
  const cargando = !negocio;

  return (
    <section aria-labelledby="t-negocio" className={classNames('card !p-5 sm:!p-6 min-w-0 flex flex-col gap-5', className)}>
      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 id="t-negocio" className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">El negocio</h2>
          <Link to="/finance" className="text-sm text-sereno-dark/60 hover:text-sereno-dark transition-colors">Abrir Finance</Link>
        </div>
        <p className="mt-1 text-xs text-sereno-dark/60">
          {f.dia === 1 ? `El 1 de ${MESES[f.mes]}` : `Del 1 al ${f.dia} de ${MESES[f.mes]}`}, frente al mismo tramo de {f.anio - 1} · pisos activos, sin Alicante
        </p>
      </div>

      {/* Volumen de reservas: el nombre que tiene en Finance desde que existe «Facturación Sereno» */}
      <div>
        <p className="text-sm text-sereno-dark/60">Volumen de reservas del mes</p>
        {cargando ? (
          <Barra className="h-10 w-48 mt-1" />
        ) : cur && prev ? (
          <>
            <div className="flex flex-wrap items-baseline gap-2.5">
              <p className="text-4xl font-bold tracking-tight leading-tight tabular-nums">{euros(cur.facturacion)}</p>
              <Variacion valor={deltas?.facturacion?.pct} grande />
            </div>
            <p className="mt-1 text-xs text-sereno-dark/60">
              Lo que pagan los huéspedes, con IVA · {euros(prev.facturacion)} hace un año
            </p>
          </>
        ) : (
          <NoDisponible que="La comparativa del mes" />
        )}

        {negocio?.anio ? (
          <div
            role="img"
            aria-label={`Volumen de reservas de los últimos 12 meses, de ${nombreLargo(meses[0])} a ${MESES[f.mes]}. El máximo es ${nombreLargo(mesMaximo)}, ${euros(mesMaximo.valor)}; ${MESES[f.mes]} va por ${euros(meses[11].valor)}.`}
            className="flex items-end gap-1 sm:gap-1.5 h-32 mt-3.5"
          >
            {meses.map((m) => (
              <div key={m.clave} title={`${nombreLargo(m)}: ${euros(m.valor)}`} className="flex-1 min-w-0 h-32 flex flex-col justify-end items-center gap-1">
                <span className="text-[10px] font-semibold whitespace-nowrap">
                  {(m.actual || m.clave === mesMaximo.clave) && m.valor > 0 ? k(m.valor) : ''}
                </span>
                <span
                  className="block w-full max-w-[26px] rounded-t"
                  style={{ height: `${Math.max(3, Math.round((m.valor / maximo) * 92))}px`, background: colorBarra(m.actual) }}
                />
                <span className="text-[10px] text-sereno-dark/50">{MESES_CORTOS[m.mes]}</span>
              </div>
            ))}
          </div>
        ) : cargando ? (
          <Barra className="h-32 mt-3.5" />
        ) : (
          <div className="mt-3"><NoDisponible que="Los últimos 12 meses" /></div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2.5">
        {cargando ? (
          [0, 1, 2, 3].map((i) => <Barra key={i} className="h-[76px]" />)
        ) : (
          <>
            {negocio?.facturacion ? (
              <Tesela
                titulo="Facturación Sereno"
                valor={euros(negocio.facturacion.total)}
                pie={
                  <p className="text-xs text-sereno-dark/60">
                    {negocio.facturacion.previsto > 0
                      ? `y ${euros(negocio.facturacion.previsto)} previstos a fin de mes`
                      : 'Base imponible, como en Holded'}
                  </p>
                }
              />
            ) : (
              <Tesela titulo="Facturación Sereno" valor="—" pie={<p className="text-xs text-sereno-dark/50">No disponible ahora</p>} />
            )}
            {cur && prev ? (
              <>
                <Tesela titulo="Ocupación" valor={`${Math.round(cur.ocupacion)} %`} pie={<Variacion valor={cur.ocupacion - prev.ocupacion} unidad="puntos" />} />
                <Tesela titulo="Precio medio por noche" valor={euros(cur.adr)} pie={<Variacion valor={deltas?.adr?.pct} />} />
                <Tesela titulo="Ingreso por casa y noche" valor={euros(revpar(cur))} pie={<Variacion valor={pctRevpar} />} />
              </>
            ) : (
              <div className="col-span-2 self-center"><NoDisponible que="Ocupación y precios" /></div>
            )}
          </>
        )}
      </div>

      <div className="flex flex-col gap-3 pt-3.5 border-t border-gray-100">
        <div className="flex items-baseline justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium">Reservado a futuro</p>
            <p className="mt-0.5 text-xs text-sereno-dark/60">
              {cargando ? '…' : cartera ? `${cartera.reservas} ${cartera.reservas === 1 ? 'reserva' : 'reservas'} con entrada después de hoy` : 'No disponible ahora'}
            </p>
          </div>
          {cartera && <p className="text-lg font-bold whitespace-nowrap tabular-nums">{euros(cartera.facturacion)}</p>}
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium">Reseñas, últimos 12 meses</p>
            <p className="mt-0.5 text-xs text-sereno-dark/60">
              {cargando
                ? '…'
                : !resenas
                  ? 'No disponible ahora'
                  : resenas.peorSubnota
                    ? `La nota más floja: ${resenas.peorSubnota.etiqueta.toLowerCase()}, ${decimal(resenas.peorSubnota.sobre5)}`
                    : `${resenas.nResenas} reseñas`}
            </p>
          </div>
          {resenas?.nota5 != null && (
            <p className="text-lg font-bold whitespace-nowrap tabular-nums">
              {decimal(resenas.nota5)}
              {resenas.delta != null && Math.abs(resenas.delta) >= 0.005 && (
                <span className={classNames('ml-1.5 text-xs font-semibold', resenas.delta > 0 ? 'text-green-700' : 'text-red-700')}>
                  <span className="sr-only">{resenas.delta > 0 ? 'Sube' : 'Baja'} frente a los 12 meses anteriores:</span>
                  {resenas.delta > 0 ? '+' : '−'}{decimal(Math.abs(resenas.delta))}
                </span>
              )}
            </p>
          )}
        </div>
      </div>

      <div>
        <p className="mb-2 text-sm font-medium">Canales del mes</p>
        {cargando ? (
          <Barra className="h-3" />
        ) : canales.length ? (
          <>
            <div
              role="img"
              aria-label={`Reparto por canal: ${canales.map((c) => `${c.nombre} ${c.redondo} %`).join(', ')}.`}
              className="flex gap-0.5 h-3 rounded-md overflow-hidden"
            >
              {canales.map((c) => (
                <span key={c.nombre} title={`${c.nombre}: ${c.redondo} %`} className="block h-full" style={{ flex: `${c.pct} 1 0`, background: c.color }} />
              ))}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-2.5" aria-hidden="true">
              {canales.map((c) => (
                <span key={c.nombre} className="inline-flex items-center gap-1.5 text-xs">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: c.color }} />
                  {c.nombre} <span className="text-sereno-dark/60">{c.redondo} %</span>
                </span>
              ))}
            </div>
          </>
        ) : cur ? (
          <p className="text-xs text-sereno-dark/50">Aún no hay reservas este mes.</p>
        ) : (
          <NoDisponible que="Los canales" />
        )}
      </div>
    </section>
  );
}
